import { useEffect, useMemo, useState } from 'react';
import { addressLines } from './planValues.js';
import {
  useIntakeItemQuery, useEditIntakeItemMutation, useDecideIntakeItemMutation, useRecheckIntakeItemMutation, useCrmTagsQuery, useUpdateContactTypeMutation,
} from './api.js';
import { CrmTagPicker, EMPTY_VOCABULARY } from './CrmTagPicker.js';
import {
  INTAKE_KINDS, fromSelection, labelFor, reachesCrm, toSelection, withdrawnNote,
} from './crmTagSelection.js';
import { PhoneListEditor } from './PhoneListEditor.js';
import { OrganizeContact } from './OrganizeContact.js';
import { ContactTypeField } from './ContactTypeField.js';
import { SharedAddressConfirm } from './SharedAddress.js';
import { contactTypeBlocks, contactTypeView, labelOf } from './contactType.js';
import {
  fromCandidatePhones, invalidPhoneError, phonesChanged, serializePhones, type PhoneRow,
} from './phoneList.js';
import {
  DrawerActions, DrawerFooter, DrawerSource, DrawerSurface, useDrawerDirty, useDrawerGuard,
} from './ReviewDrawer.js';
import { Alert, Button, Spinner } from './ui.js';

/**
 * One contact, and the decision waiting on it.
 *
 * The words are the customer's throughout. What the system calls a plan hash and a policy
 * outcome, this screen calls "what will happen" and "why this needs you".
 */

const REASON_COPY: Record<string, string> = {
  relationship_conflict: 'Your CRM already has this person with a different tag.',
  tag_not_allowed_for_relationship: 'A suggested tag is not one of your CRM tags.',
  identity_unresolved: 'We could not tell which contact this is.',
  would_create_contact: 'This would add a new contact to your CRM.',
  low_confidence: 'The details are not clear enough to act on without you.',
  calibration_mode: 'This workspace reviews everything before it acts.',
  contact_type_required: 'Choose a Contact Type for this new contact.',
  contact_type_unsupported: 'The Contact Type in the file is not one your CRM offers. Choose one from the list.',
  shared_address_unconfirmed: 'This email may be shared. Confirm the person before sending.',
  shared_address_confirmed: 'You confirmed this person uses a shared email.',
  shared_address_in_use: 'More than one Keap contact uses this email. Choose the correct person.',
};

/** Where an imported contact came from, as context. */
export const CAPTURED_FROM: Record<string, string> = {
  csv_import: 'File Import',
  business_card: 'Business Card',
  contact_list_image: 'Photo',
};

const FIELD_LABEL: Record<string, string> = {
  givenName: 'First name', familyName: 'Last name', company: 'Company',
  jobTitle: 'Job title', emails: 'Email', phones: 'Phone numbers', website: 'Website',
  contactType: 'Contact Type',
};

/** A planned field value in words. A list (of numbers, of addresses) is joined, never "[object Object]". */
function shown(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.map(shown).filter(Boolean).join(', ');
  if (typeof v === 'object') {
    // An address has no single value: read as the confirm screens read it, so it is never blank.
    if ('line1' in v || 'locality' in v || 'postalCode' in v) return addressLines(v).join(', ');
    const o = v as { e164?: unknown; raw?: unknown; normalized?: unknown; value?: unknown };
    return String(o.e164 ?? o.normalized ?? o.value ?? o.raw ?? '');
  }
  return String(v);
}

export function ItemDetail({ itemId, onBack, onDecided }: {
  itemId: string; onBack: () => void;
  /** A decision went through: the import takes it from here, and says what happened. */
  onDecided?: (message: string) => void;
}) {
  const { data, isLoading, error, refetch } = useIntakeItemQuery(itemId);
  const [edit, { isLoading: saving }] = useEditIntakeItemMutation();
  const [recheck, { isLoading: rechecking }] = useRecheckIntakeItemMutation();
  const [decide, { isLoading: deciding }] = useDecideIntakeItemMutation();
  const [updateType, { isLoading: updatingType }] = useUpdateContactTypeMutation();
  /** A proposed Contact Type change for this row's Keap contact: the contact and value seen, the value chosen. */
  const [typeProposal, setTypeProposal] = useState<{ ext: string; from: string | null; to: string; requestId: string } | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  /** Every number, in order, each with its type. Seeded from the candidate; saved as a list. */
  const [phones, setPhones] = useState<PhoneRow[]>([]);
  const [initialPhones, setInitialPhones] = useState<PhoneRow[]>([]);
  const [phoneError, setPhoneError] = useState<{ message: string; values: string[] } | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [savingTags, setSavingTags] = useState(false);
  const tags = useCrmTagsQuery();
  const vocab = tags.data ?? EMPTY_VOCABULARY;

  const candidate = data?.item.candidate ?? null;

  /*
   * A proposal is of one contact and one value as seen. If Keap's value or the matched
   * contact moved since, it is dropped and the person is told, never carried over.
   */
  useEffect(() => {
    if (!data || !typeProposal) return;
    const live = data.existingContact ?? null;
    if (live && live.status === 'ok' && live.externalContactId === typeProposal.ext && live.contactType === typeProposal.from) return;
    setTypeProposal(null);
    setNotice({ tone: 'error', text: 'This contact changed in Keap. Review the latest information before continuing.' });
  }, [data]);

  useEffect(() => {
    if (!candidate) return;
    setForm({
      firstName: candidate.givenName ?? '',
      lastName: candidate.familyName ?? '',
      email: candidate.emails[0]?.normalized ?? '',
      company: candidate.company ?? '',
      jobTitle: (candidate as { jobTitle?: string | null }).jobTitle ?? '',
    });
    const rows = fromCandidatePhones(candidate.phones);
    setPhones(rows);
    setInitialPhones(rows);
  }, [candidate]);

  const dirty = useMemo(() => {
    if (!candidate) return false;
    return form.firstName !== (candidate.givenName ?? '')
      || form.lastName !== (candidate.familyName ?? '')
      || form.email !== (candidate.emails[0]?.normalized ?? '')
      || form.company !== (candidate.company ?? '')
      || form.jobTitle !== ((candidate as { jobTitle?: string | null }).jobTitle ?? '')
      || phonesChanged(phones, initialPhones);
  }, [form, candidate, phones, initialPhones]);
  // Leaving with unsaved corrections — Back to the import, or closing — asks first.
  useDrawerDirty(dirty);
  const guard = useDrawerGuard();

  if (isLoading) return <Spinner label="Loading contact…" />;
  if (error || !data) return <Alert tone="error">That contact could not be loaded.</Alert>;

  const { item, crmWriteEnabled } = data;
  const decided = item.approvalState !== null;

  const save = async () => {
    setNotice(null);
    setPhoneError(null);
    try {
      // The list replaces the old one, in order — sent only when it changed.
      const patch = phonesChanged(phones, initialPhones)
        ? { ...form, phones: serializePhones(phones) } : form;
      await edit({ id: itemId, patch }).unwrap();
      await refetch();
      setNotice({ tone: 'success', text: 'Saved. This contact was checked again.' });
    } catch (e) {
      const bad = invalidPhoneError(e);
      if (bad) setPhoneError(bad);
      else setNotice({ tone: 'error', text: 'That change could not be saved.' });
    }
  };

  /**
   * Sets the CRM tags, through the same endpoint every other correction uses.
   *
   * `PUT /intake/items/:id` accepts the stage (`relationship`) and the generic tags
   * (`tagKeys`), bumps the candidate version, regenerates the plan and its hash, and clears
   * any approval. Image and file capture take no relationship types, so the picker offers
   * none.
   */
  const tagIds = toSelection(
    { commercialStage: item.relationship, relationshipTypeKeys: [], crmTagKeys: item.tagKeys },
    vocab, INTAKE_KINDS,
  );
  const setTags = async (ids: string[]) => {
    setNotice(null);
    setSavingTags(true);
    const f = fromSelection(ids, vocab, INTAKE_KINDS);
    try {
      await edit({ id: itemId, patch: { relationship: f.commercialStage, tagKeys: f.crmTagKeys } }).unwrap();
      await refetch();
      setNotice({ tone: 'success', text: 'CRM tags saved. This contact was checked again.' });
    } catch {
      setNotice({ tone: 'error', text: 'Those CRM tags could not be saved.' });
    } finally {
      setSavingTags(false);
    }
  };

  const planTags = item.plan
    ? toSelection({ commercialStage: item.plan.relationship, relationshipTypeKeys: [], crmTagKeys: item.plan.tagKeys }, vocab, INTAKE_KINDS)
      .filter((id) => reachesCrm(id, vocab)).map((id) => labelFor(id, vocab))
    : [];

  /** The person's Contact Type change for this row's Keap contact, sent only by approval. */
  const runTypeUpdate = async (): Promise<string | null> => {
    if (!typeProposal) return null;
    try {
      const r = await updateType({ externalContactId: typeProposal.ext, expected: typeProposal.from, contactType: typeProposal.to,
        requestId: typeProposal.requestId, source: { kind: 'intake', itemId } }).unwrap();
      return r.status === 'checking'
        ? 'Keap did not confirm the Contact Type change yet. ContactPilot will check before doing anything else.'
        : 'Contact Type updated in Keap.';
    } catch (e) {
      const body = (e as { data?: { error?: string; message?: string } })?.data;
      return body?.error === 'contact_type_changed'
        ? 'This contact changed in Keap. Review the latest Contact Type before updating.'
        : body?.message ?? 'We couldn’t update the Contact Type. Review the issue before trying again.';
    } finally {
      setTypeProposal(null);
    }
  };

  /** Nothing else to send for this row: approving makes only the Contact Type change. */
  const approveTypeOnly = async () => {
    setNotice(null);
    const typed = await runTypeUpdate();
    await refetch();
    if (typed) setNotice({ tone: typed.startsWith('Contact Type updated') ? 'success' : 'error', text: typed });
  };

  const act = async (state: 'approved' | 'rejected') => {
    setNotice(null);
    try {
      const res = await decide({ id: itemId, state, planHash: item.planHash }).unwrap();
      const typed = state === 'approved' ? await runTypeUpdate() : null;
      if (typed && !typed.startsWith('Contact Type updated')) { await refetch(); setNotice({ tone: 'error', text: typed }); return; }
      const text = res.alreadyDecided ? 'This was already decided.'
        : state === 'approved'
          ? (res.destinationOnly ? 'Approved. Sending to your Zapier and webhook destinations.'
            : crmWriteEnabled
              ? 'Approved. This will be sent to your CRM.'
              : 'Approved. Writing to your CRM is switched off, so nothing has been sent.')
          : item.plan?.action === 'deliver' ? 'Marked as no action. Nothing will be sent.'
            : 'Marked as no action. Nothing will be sent to your CRM.';
      if (onDecided) { onDecided(typed ? `${text} ${typed}` : text); return; }
      await refetch();
      setNotice({ tone: 'success', text: typed ? `${text} ${typed}` : text });
    } catch (e) {
      const failure = (e as { data?: { error?: string; message?: string } }).data;
      // A destination plan that went stale says why; the new plan is already on screen.
      if (failure?.error === 'stale_plan') await refetch();
      setNotice({
        tone: 'error',
        text: failure?.error === 'stale_plan'
          ? (failure.message && item.plan?.action === 'deliver' ? failure.message : 'This contact changed since you opened it. Check it again before approving.')
          : 'That could not be recorded.',
      });
    }
  };

  /*
   * Contact Type: a new contact's needs a person's choice (Keap would otherwise default
   * it); an existing contact's is the CRM's and is shown as it is. A value from the file
   * that the CRM does not offer is shown as such, never translated.
   */
  const existing = data.existingContact ?? null;
  const typeStatus: 'new' | 'existing' | 'other' = item.plan?.action === 'create' ? 'new'
    : item.plan?.action === 'update' || existing ? 'existing' : 'other';
  const chosenType = (candidate as { crmContactType?: string | null } | null)?.crmContactType ?? null;
  const typeLabel = (v: string | null) => (v ? (data.contactTypes ? labelOf(data.contactTypes, v) : v) : 'Not set');
  /*
   * The file's own Contact Type for a contact already in Keap: shown as where it came from,
   * never applied. Keap's value stays unless the person chooses a change and approves it.
   */
  const fileType = typeStatus === 'existing' && chosenType
    && (candidate as { crmContactTypeSource?: string | null } | null)?.crmContactTypeSource !== 'workspace_default'
    && existing?.status === 'ok' && chosenType !== existing.contactType ? chosenType : null;
  const typeChange = typeProposal
    ? `Update existing Keap contact: ${[candidate?.givenName, candidate?.familyName].filter(Boolean).join(' ') || candidate?.fullName || 'this contact'} — Contact Type ${typeLabel(typeProposal.from)} → ${typeLabel(typeProposal.to)}`
    : null;
  // Only a Contact Type change to make: the row has no plan of its own to approve.
  const typeOnly = Boolean(typeProposal) && !item.planHash;
  const typeView = contactTypeView({
    types: data.contactTypes, status: typeStatus, value: chosenType, existingValue: data.existingContactType ?? null,
  });
  const setContactType = async (contactType: string | null) => {
    setNotice(null);
    setSavingTags(true);
    try {
      await edit({ id: itemId, patch: { contactType } }).unwrap();
      await refetch();
      setNotice({ tone: 'success', text: 'Contact Type saved. This contact was checked again.' });
    } catch {
      setNotice({ tone: 'error', text: 'That Contact Type could not be saved.' });
    } finally {
      setSavingTags(false);
    }
  };
  /** A shared-address decision for this one row, saved at once; the row is checked again. */
  const setShared = async (patch: { confirmSharedAddress?: boolean; keapContactId?: string }) => {
    setNotice(null);
    setSavingTags(true);
    try {
      await edit({ id: itemId, patch }).unwrap();
      await refetch();
      setNotice(patch.keapContactId
        ? { tone: 'success', text: 'Matched to that Keap contact. Nothing has been sent yet.' }
        : patch.confirmSharedAddress
          ? { tone: 'success', text: 'Confirmed. Keap was checked for this person. Nothing has been sent yet.' }
          : { tone: 'info', text: 'Skipped. This person won’t be sent.' });
    } catch {
      setNotice({ tone: 'error', text: 'That could not be saved. Check the name and email, then try again.' });
    } finally {
      setSavingTags(false);
    }
  };
  const sharedNeedsPerson = Boolean(data.sharedAddress && (!data.sharedAddress.confirmed || data.sharedAddress.match !== 'new' && data.sharedAddress.match !== 'existing'));
  const blocked = decided ? null : ((sharedNeedsPerson ? 'This email may be shared. Confirm the person before sending.' : null)
    ?? withdrawnNote(tagIds, vocab) ?? contactTypeBlocks([typeView]));

  return (
    <div className="min-w-0 space-y-4">
      <button
        type="button" onClick={() => guard(onBack)}
        className="inline-flex min-h-11 items-center text-sm text-slate-600 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
      >
        ← Back to the import
      </button>

      {/* Where they came from: once, in the drawer's header. */}
      <DrawerSource>
        <p className="text-xs text-slate-500" data-captured-from="">
          Captured from {CAPTURED_FROM[item.source] ?? 'File Import'}
          {item.sourceRef ? ` · ${item.sourceRef.replace('row:', 'row ')}` : ''}
        </p>
      </DrawerSource>

      {/* Who. */}
      <header className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-slate-900" title={candidate?.fullName ?? undefined}>
            {candidate?.fullName ?? 'No name'}
          </h3>
        </div>
        {decided ? (
          <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
            {/*
              * Follows what actually happened, not merely that somebody approved.
              *
              * This said "Approved — sync disabled" for every approved item regardless
              * of outcome, which was true only while nothing had ever been written.
              * The first successful CRM write made it a lie on the one screen a person
              * opens to find out whether the contact reached their CRM.
              */}
            {item.approvalState !== 'approved' ? 'No action'
              : item.status === 'completed' ? (item.plan?.action === 'deliver' ? 'Sent to your destinations' : 'Sent to your CRM')
              : item.status === 'held' ? 'Approved — sync paused'
              : item.status === 'failed' ? (item.plan?.action === 'deliver' ? 'Approved — a destination refused it' : 'Approved — could not be sent')
              : 'Approved'}
          </span>
        ) : null}
      </header>

      {item.reviewReasons.length > 0 && !decided ? (
        <div>
          <p className="text-sm font-medium text-slate-700">Why this needs you</p>
          <ul className="mt-1 list-inside list-disc text-sm text-slate-600">
            {item.reviewReasons.map((r) => <li key={r}>{REASON_COPY[r] ?? r.replace(/_/g, ' ')}</li>)}
          </ul>
        </div>
      ) : null}

      <DrawerSurface className="min-w-0 space-y-5">
        {/* ── The contact ────────────────────────────────────────────────── */}
        <section className="min-w-0" aria-label="Contact details">
          <p className="text-sm font-medium text-slate-700">Contact details</p>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {([
              ['firstName', 'First name'], ['lastName', 'Last name'],
              ['email', 'Email'], ['company', 'Company'], ['jobTitle', 'Job title'],
            ] as const).map(([key, label]) => (
              <label key={key} className="block min-w-0 text-sm">
                <span className="text-slate-600">{label}</span>
                <input
                  type="text"
                  value={form[key] ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 px-2 text-base sm:text-sm"
                />
              </label>
            ))}
          </div>
          <div className="mt-4">
            <PhoneListEditor
              rows={phones}
              onChange={(rows) => { setPhones(rows); setPhoneError(null); }}
              deliveries={data.phones}
              error={phoneError}
            />
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Correcting a contact checks it again, and clears any approval — what you agreed
            to was based on the old details.
          </p>
          <div className="mt-3">
            <Button className="min-h-11" onClick={save} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </section>

        {/* ── The CRM tags this contact gets ────────────────────────────── */}
        {!decided ? (
          <div className="border-t border-slate-100 pt-4" data-crm-tags-section="">
            <CrmTagPicker
              vocab={vocab}
              selected={tagIds}
              kinds={INTAKE_KINDS}
              onChange={(ids) => void setTags(ids)}
              disabled={savingTags}
              legend="CRM Tags"
              empty="No CRM tags."
            />
            <p className="mt-1 text-xs text-slate-500">
              {/*
                Saying plainly that this is not cosmetic: the tags are part of what would
                be sent, so the plan below is recalculated and any approval is withdrawn.
              */}
              Changing these recalculates what would be sent to your CRM.
            </p>
          </div>
        ) : null}

        {/* ── How the person organises this contact ─────────────────────── */}
        {!decided ? (
          <OrganizeContact
            value={candidate?.organization}
            disabled={saving}
            saving={saving}
            onSave={async (organization) => {
              setNotice(null);
              try {
                await edit({ id: itemId, patch: { organization } }).unwrap();
                await refetch();
                setNotice({ tone: 'success', text: 'Saved. This contact was checked again.' });
              } catch {
                setNotice({ tone: 'error', text: 'That change could not be saved.' });
              }
            }}
          />
        ) : null}

        {!decided && data.sharedAddress ? (
          <div data-item-shared="">
            <SharedAddressConfirm
              name={[candidate?.givenName, candidate?.familyName].filter(Boolean).join(' ') || (candidate?.fullName ?? '')}
              email={candidate?.emails?.[0]?.normalized ?? ''}
              state={data.sharedAddress}
              editable={!savingTags && !saving}
              busy={savingTags}
              onConfirm={() => void setShared({ confirmSharedAddress: true })}
              onSkip={() => void setShared({ confirmSharedAddress: false })}
              onChoose={(keapContactId) => void setShared({ keapContactId })}
              idBase={`item-${itemId}`}
            />
          </div>
        ) : null}

        {!decided && typeView.kind !== 'hidden' ? (
          <div data-item-contact-type="">
            <ContactTypeField
              types={data.contactTypes}
              status={typeStatus}
              value={chosenType}
              existingValue={data.existingContactType ?? null}
              disabled={savingTags || saving}
              onChange={(v) => void setContactType(v)}
              idBase={`item-${itemId}`}
              {...(existing ? {
                keap: { status: existing.status, contactType: existing.contactType },
                proposed: typeProposal?.to ?? null,
                onRetryRead: () => { void refetch(); },
                onPropose: (to: string | null) => setTypeProposal(to
                  ? { ext: existing.externalContactId, from: existing.contactType, to, requestId: typeProposal?.requestId ?? crypto.randomUUID() }
                  : null),
              } : {})}
            />
            {fileType ? (
              <p className="mt-1 text-xs text-slate-500 [overflow-wrap:anywhere]" data-file-contact-type="">
                Your file says {typeLabel(fileType)}. Not applied: Keap keeps {typeLabel(existing?.contactType ?? null)} unless you change it here.
              </p>
            ) : null}
          </div>
        ) : null}

        {/* ── What would happen ─────────────────────────────────────────── */}
        <div className="rounded-md bg-slate-50 p-3">
          <p className="text-sm font-medium text-slate-800">
            {item.plan?.action === 'deliver' ? 'Send to your Zapier and webhook destinations'
              : item.plan?.action === 'update' ? 'Update existing contact'
              : item.plan?.action === 'create' ? 'New contact'
                : item.status === 'duplicate' ? 'No action — already in this file'
                  : existing ? 'Already in Keap — nothing else to send'
                    : 'No action'}
          </p>
          {typeChange ? (
            <p className="mt-1 text-sm font-medium text-slate-800 [overflow-wrap:anywhere]" data-type-change="">{typeChange}</p>
          ) : null}
          {planTags.length > 0 ? (
            <p className="mt-1 text-xs text-slate-600 [overflow-wrap:anywhere]">CRM tags: {planTags.join(', ')}</p>
          ) : null}
          {item.plan && item.plan.fieldChanges.length > 0 ? (
            <dl className="mt-2 space-y-1 text-xs">
              {item.plan.fieldChanges.map((c) => (
                <div key={c.field} className="flex min-w-0 flex-wrap gap-x-2">
                  <dt className="text-slate-500">{FIELD_LABEL[c.field] ?? c.field}</dt>
                  <dd className="min-w-0 text-slate-700 [overflow-wrap:anywhere]">
                    {shown(c.current) ? <s className="text-slate-400">{shown(c.current)}</s> : null}
                    {shown(c.current) ? ' → ' : ''}
                    {shown(c.proposed)}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
          {item.plan?.action === 'deliver' ? (
            <p className="mt-2 text-xs text-slate-500 [overflow-wrap:anywhere]" data-destination-only="">
              No CRM is connected. Approving sends this contact to{' '}
              {(item.plan as { destinations?: Array<{ name: string }> }).destinations?.map((d) => d.name).join(', ') || 'your destinations'}
              {' '}(Settings → Integrations).
            </p>
          ) : !crmWriteEnabled ? (
            <p className="mt-2 text-xs text-slate-500">
              Sending to your CRM is switched off, so nothing will be sent.
            </p>
          ) : null}
        </div>
      </DrawerSurface>

      {notice ? <Alert tone={notice.tone}>{notice.text}</Alert> : null}

      {/* ── Decide ───────────────────────────────────────────────────── */}
      {!decided ? (
        <DrawerFooter>
          <DrawerActions
            primary={typeOnly ? (
              <Button onClick={() => void approveTypeOnly()} disabled={updatingType || dirty}>
                {updatingType ? 'Approving…' : 'Approve change'}
              </Button>
            ) : (
              <Button onClick={() => act('approved')} disabled={deciding || updatingType || !item.planHash || Boolean(blocked) || dirty}>
                Approve
              </Button>
            )}
            secondary={[
              <Button key="none" variant="secondary" onClick={() => act('rejected')} disabled={deciding}>
                No action
              </Button>,
            ]}
          >
            {blocked ? <p className="text-xs text-amber-900 [overflow-wrap:anywhere]" data-blocked-note="">{blocked}</p> : null}
            {dirty && !blocked ? <p className="text-xs text-slate-600">Save your changes before approving.</p> : null}
            {!item.planHash && !typeOnly ? (
              <p className="text-xs text-slate-500" data-no-plan="">
                {existing ? 'Nothing else to send. Change the Contact Type above to update it in Keap.'
                  : data.delivery && !data.delivery.crmConnected
                    ? (data.delivery.activeDestinations > 0
                      ? 'A destination is now connected. Check again to see where this contact would be sent.'
                      : 'Saved in ContactPilot. It has not been sent anywhere: connect a CRM or add a Zapier or webhook destination in Settings → Integrations to send it.')
                    : 'There is nothing to approve for this contact.'}
              </p>
            ) : null}
            {!item.planHash && !typeOnly && data.delivery && !data.delivery.crmConnected && data.delivery.activeDestinations > 0 ? (
              <Button variant="secondary" disabled={rechecking} onClick={async () => { await recheck(itemId).unwrap().catch(() => null); await refetch(); }}>
                {rechecking ? 'Checking…' : 'Check again'}
              </Button>
            ) : null}
          </DrawerActions>
        </DrawerFooter>
      ) : null}
    </div>
  );
}
